import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormControl, Select as SelectMui } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { useTheme } from 'contexts/ThemeContext';

import pluralize from 'utils/pluralize';

import { HelpTooltip, Text, Tooltip } from 'components/DataDisplay';
import { Button, TextField } from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';

import { Loading } from '../Loading';

import { Checkbox } from './Checkbox';

const CustomOption = ({ value: { value, label }, onChange, checked }) => {
  const { panelColor, themeColors } = useTheme();
  const colorRules = themeColors[panelColor];
  return (
    <Row
      alignItems="center"
      justify="flex-start"
      spacing={0}
      style={{ padding: '0px 9px' }}
    >
      <Item flex>
        <Checkbox
          color={colorRules}
          checked={checked}
          onChange={() => onChange({ value, label })}
        />
      </Item>
      <Item xs justify="flex-start">
        <Tooltip title={label ?? value}>
          <Text fontSize="13px" nowrap>
            {label ?? value}
          </Text>
        </Tooltip>
      </Item>
    </Row>
  );
};

export const MultipleSelectSearch = props => {
  const {
    title,
    required,
    tooltip,
    disabled,
    small,
    bgColor,
    placeholder,
    color,
    values = [],
    onChange,
    type = '',
    options: propOptions, // <-- si options passées directement alors fetchOptions ne sera pas utilisé
    fetchOptions, // <-- fonction fetch async (optionnelle)
    debounceTime = 300,
    limit = 20,
    warningColor,
    selectAll
  } = props;

  const { panelColor, themeColors } = useTheme();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState(values);
  const [options, setOptions] = useState([]);
  const [optionsDisp, setOptionsDisp] = useState([]);
  const [search, setSearch] = useState('');
  const [totalCount, setTotalCount] = useState(0);

  const colorRules = themeColors[color] || color || themeColors[panelColor];

  const useStyles = makeStyles({
    root: {
      '& .MuiInputBase-root': {
        color: themeColors.light,
        backgroundColor: bgColor || 'transparent'
      },
      '& .MuiSelect-icon': {
        color: themeColors.light
      },
      '& .MuiInput-underline:after': {
        borderBottomColor: colorRules
      },
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: colorRules
        },
        '&:hover fieldset': {
          borderColor: themeColors.light
        },
        '&.Mui-focused fieldset': {
          borderColor: colorRules
        }
      }
    }
  });

  const classes = useStyles();

  const onCheck = ({ value, label }) => {
    const tmp = [...selectedItems];
    const existIndex = selectedItems.findIndex(el => el.value === value);
    if (existIndex !== -1) {
      setSelectedItems(tmp.filter(el => !(el.value === value)));
      return;
    }
    tmp.push({ value, label });
    setSelectedItems(tmp);
  };

  const filterOptionsBySearch = (items, searchValue) => {
    const purify = str => {
      return typeof str === 'string'
        ? str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        : '';
    };

    const regex = '[A-Za-zÀ-ÖØ-öø-ÿ0-9]+';
    const tbExp = searchValue.match(new RegExp(regex, 'gi')) || [];

    const result = items.filter(item => {
      for (let i = 0; i < tbExp.length; i += 1) {
        const searchedWord = purify(tbExp[i].toUpperCase());
        if (!new RegExp(searchedWord, 'gi').test(item.label)) {
          return false;
        }
      }
      return true;
    });
    return result;
  };

  const onSearch = value => {
    setSearch(value);
    if (typeof fetchOptions !== 'function') {
      const result = filterOptionsBySearch(options, value);
      setOptionsDisp(result);
    }
  };

  const onSelectAll = () => {
    let newOptions = [];
    if (options.length !== selectedItems.length) {
      newOptions = [...options];
    }
    setSelectedItems(newOptions);
  };

  useEffect(() => {
    setSelectedItems(values);
  }, [values]);

  useEffect(() => {
    const result = filterOptionsBySearch(options, search);
    setOptionsDisp(result);
    // eslint-disable-next-line
  }, [options]);

  // Sync options prop changes (pour mode statique)
  useEffect(() => {
    if (propOptions) {
      setOptions(propOptions);
      setTotalCount(propOptions.length);
    }
  }, [propOptions]);

  // Si fetchOptions est fourni, on fait l’async fetch au lieu d’utiliser propOptions
  useEffect(() => {
    // pas d’appel async si pas fourni
    if (!fetchOptions) {
      return;
    }

    if (!open) {
      setOptions([]);
      return;
    }

    const controller = new AbortController();
    const handler = setTimeout(() => {
      setLoading(true);
      fetchOptions(search)
        .then(results => {
          setOptions(results.data);
          setTotalCount(results.totalCount);
          setLoading(false);
        })
        .catch(err => {
          if (err.name !== 'AbortError') {
            console.error(err);
          }
          setLoading(false);
        });
    }, debounceTime);

    // eslint-disable-next-line consistent-return
    return () => {
      controller.abort();
      clearTimeout(handler);
    };
    // eslint-disable-next-line
  }, [search, open, debounceTime]);

  return (
    <FormControl
      size="small"
      variant="outlined"
      className={classes.root}
      style={{
        padding: small && '6px 0px',
        width: '100%'
      }}
    >
      {title && (
        <Row spacing={2} justify="flex-start" alignItems="center">
          <Item justify="flex-start" flex>
            <Text noWrap fontSize="14px">
              {title === '&nbsp;' ? <>&nbsp;</> : title}
              {required && '*'}
            </Text>
          </Item>
          {tooltip && (
            <Item
              flex
              style={{
                alignItem: 'center'
              }}
            >
              <HelpTooltip title={tooltip} />
            </Item>
          )}
        </Row>
      )}
      <SelectMui
        disabled={disabled}
        displayEmpty
        style={{
          height: small && '40px'
        }}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
          },
          PaperProps: {
            style: {
              width: anchorEl ? anchorEl.clientWidth : undefined,
              maxWidth: anchorEl ? anchorEl.clientWidth : undefined
            }
          }
        }}
        multiple
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        onOpen={e => {
          setOpen(true);
          setAnchorEl(e.currentTarget);
        }}
        renderValue={() => {
          let displayText = placeholder || '';

          if (selectedItems.length === options.length) {
            displayText = 'Tous';
          } else if (selectedItems.length > 0) {
            displayText = selectedItems.map(e => e.label).join(', ');
          }

          return (
            <Text
              nowrap
              style={{
                maxWidth: '200px',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                textAlign: 'justify'
              }}
            >
              {displayText}
            </Text>
          );
        }}
        value={selectedItems}
      >
        <Container spacing={0}>
          <Row spacing={0}>
            <Item>
              <TextField
                width="95%"
                placeholder="Rechercher..."
                fontSize="smaller"
                clearable
                small
                value={search}
                onChange={onSearch}
                color={themeColors.themeV2.inputColor}
              />
            </Item>
          </Row>

          <Row
            spacing={0}
            style={{
              maxHeight: '300px',
              overflowY: 'auto',
              overflowX: 'hidden'
            }}
          >
            <Loading loading={loading}>
              {selectAll && search.length === 0 && (
                <CustomOption
                  value={{
                    value: 'selectAll',
                    label: 'Tout sélectionner'
                  }}
                  onChange={onSelectAll}
                  checked={options.length === selectedItems.length}
                />
              )}
              {optionsDisp.slice(0, limit || totalCount).map(option => {
                return (
                  <CustomOption
                    value={option}
                    onChange={onCheck}
                    checked={selectedItems.find(
                      el => el.value === option.value
                    )}
                  />
                );
              })}
              {limit && limit < totalCount && (
                <Text
                  style={{
                    display: 'flex'
                  }}
                >
                  +<Text color={warningColor}>{totalCount - limit}</Text>
                  &nbsp;
                  {pluralize(type, totalCount - limit)}
                  &nbsp;restantes
                </Text>
              )}
            </Loading>
          </Row>
          <Row>
            <Item>
              <Button
                variant="contained"
                small
                disabled={loading}
                onClick={() => {
                  setOpen(false);
                  onChange(selectedItems);
                }}
              >
                Valider
              </Button>
            </Item>
          </Row>
        </Container>
      </SelectMui>
    </FormControl>
  );
};

MultipleSelectSearch.defaultProps = {
  onChange: () => null,
  options: []
};

MultipleSelectSearch.propTypes = {
  onChange: PropTypes.func,
  options: PropTypes.oneOfType([PropTypes.array, PropTypes.string])
};

export default MultipleSelectSearch;
